import { networkApi } from '../infrastructure/api/NetworkApiClient';
import { ApiError } from '../infrastructure/api/BaseApiClient';

/** Register reading (cumulative, Wh) at an instant. */
export interface TafReadingPoint {
  timestamp: string;
  valueWh: number;
}

/** Energy between two readings or summed over a display bucket. */
export interface TafIntervalPoint {
  start: string;
  end: string;
  energyWh: number;
  avgPowerW: number;
  /** Highest 15-min mean inside an aggregated bucket. */
  maxPowerW: number;
}

/** One register (OBIS) of a TAF profile over the requested window. */
export interface TafRegisterSeries {
  obisCode: string;
  direction?: 'bezug' | 'einspeisung';
  tariff?: number;
  readings: TafReadingPoint[];
  intervals: TafIntervalPoint[];
  /** Bucket of `intervals` in minutes; 0 = native spacing (e.g. monthly TAF 1). */
  resolutionMinutes: number;
  energyWh: number;
  negativeSteps: number;
}

export type TafReadingsStatus = 'ok' | 'not_configured' | 'unauthorized' | 'access_denied' | 'not_found' | 'upstream_error';

/** `GET …/meters/taf-readings` — loaded from the Messstellenbetreiber on request, never stored in GridPilot. */
export interface TafReadingsResponse {
  status: TafReadingsStatus;
  message?: string;
  tafLogicalName: string;
  tafType?: string;
  meteringPointId?: string;
  smgwSerial?: string;
  from: string;
  to: string;
  fetchedAt: string;
  durationMs: number;
  fromCache: boolean;
  truncated: boolean;
  dataAvailableTo?: string;
  registers: TafRegisterSeries[];
}

/**
 * Register values (TAF 1 Zählerstände, TAF 7 Zählerstandsgang) of one profile
 * over `days` up to `anchorIso` (map time-rewind) or now. The backend asks the
 * MSB — expect a few seconds. Expected failures (not configured, MSB denies the
 * profile) come back with `status` + `message`, not as an exception.
 */
export async function getTafReadings(
  tafLogicalName: string,
  days: number,
  anchorIso?: string,
  signal?: AbortSignal,
  refresh = false,
): Promise<TafReadingsResponse> {
  let url = `/meters/taf-readings?tafLogicalName=${encodeURIComponent(tafLogicalName)}&days=${days}`;
  if (anchorIso) url += `&anchor=${encodeURIComponent(anchorIso)}`;
  if (refresh) url += '&refresh=true';
  try {
    return await networkApi.zoneGet<TafReadingsResponse>(url, { signal, timeout: 90_000 });
  } catch (error) {
    if (error instanceof ApiError) {
      throw new Error(error.status === 404
        ? 'Dieses TAF-Profil gehört zu keinem Smart Meter Gateway der Zone.'
        : `Abruf fehlgeschlagen: ${error.message}`, { cause: error });
    }
    throw error;
  }
}
